import type { PropsWithChildren } from 'react';
import clsx from 'clsx';
import { NewWorkspaceModal } from 'components/pages/NewWorkspaceModal';
import { PaymentsNavMenu } from 'components/pages/payments/PaymentsNavMenu';
import { PaymentsSettingsNav } from 'components/pages/payments/PaymentsSettingsNav';
import { PaymentsWorkspaceSwitcher } from 'components/pages/payments/PaymentsWorkspaceSwitcher';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useLocation } from 'wouter';
import { DeviceSize, useDeviceSize } from 'wrappers/DeviceSizeProvider';
import { useWorkspace } from 'wrappers/WorkspaceProvider';

import { LoadingIndicator } from '@fragment/ui/src/components/LoadingText';

import { ToastProvider } from '../../wrappers/ToastProvider';
import { PaymentsNav } from '../SideNav/PaymentsNav';

export type NavMode = 'nav' | 'menu' | 'workspaces' | 'settings';

const AmbientBlob = () => {
  const blobRef = useRef<HTMLDivElement>(null);
  const pos = useRef({ x: 0, y: 0 });
  const vel = useRef({ x: 0, y: 0 });
  const target = useRef({ x: 0, y: 0 });
  const isIdle = useRef(true);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phase = useRef(0);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      target.current = {
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      };
      isIdle.current = false;
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        isIdle.current = true;
      }, 1000);
    };

    window.addEventListener('mousemove', onMouseMove);

    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      if (isIdle.current) {
        phase.current += dt * 0.04;
        const p = phase.current;
        target.current = {
          x: Math.sin(p * 1.3) * 9 + Math.sin(p * 0.7 + 1.2) * 4,
          y: Math.cos(p * 1.1 + 0.8) * 9 + Math.cos(p * 0.5 + 2.1) * 4,
        };
      }

      const spring = 4.0;
      const damping = 3.8;
      vel.current.x +=
        ((target.current.x - pos.current.x) * spring -
          vel.current.x * damping) *
        dt;
      vel.current.y +=
        ((target.current.y - pos.current.y) * spring -
          vel.current.y * damping) *
        dt;
      pos.current.x += vel.current.x * dt;
      pos.current.y += vel.current.y * dt;

      if (blobRef.current) {
        blobRef.current.style.transform = `translate(${pos.current.x}%, ${pos.current.y}%)`;
      }

      raf.current = requestAnimationFrame(tick);
    };

    raf.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
      <div ref={blobRef} className="ambient-blob" />
    </div>
  );
};

type PaymentsLayoutProps = {
  footer?: React.ReactNode;
  showLoadingIndicator?: boolean;
  collapseLeftNav?: boolean;
};

export const PaymentsLayout = ({
  children,
  footer,
  showLoadingIndicator,
  collapseLeftNav = false,
}: PropsWithChildren<PaymentsLayoutProps>) => {
  const deviceType = useDeviceSize();
  const [isLeftNavExpanded, setLeftNavExpanded] = useState<boolean>(
    !collapseLeftNav
  );
  const [navMode, setNavMode] = useState<NavMode>('nav');
  const [location, navigate] = useLocation();
  const prevPathRef = useRef<string | null>(null);
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id;
  if (!workspaceId) {
    throw new Error('workspaceId not found');
  }
  const isSmallScreen = useMemo(
    () =>
      deviceType === DeviceSize.Phone ||
      deviceType === DeviceSize.Tablet ||
      deviceType === DeviceSize.WideTablet,
    [deviceType]
  );

  const isCollapsed = !isLeftNavExpanded;

  useEffect(() => {
    if (isSmallScreen) {
      setLeftNavExpanded(false);
    }
  }, [isSmallScreen]);

  const isOnSettings = location.includes('/settings');

  useEffect(() => {
    if (isOnSettings && navMode !== 'settings') {
      setNavMode('settings');
    } else if (!isOnSettings && navMode === 'settings') {
      setNavMode('nav');
    }
  }, [isOnSettings, navMode]);

  useEffect(() => {
    if (!isOnSettings) {
      prevPathRef.current = location;
    }
  }, [location, isOnSettings]);

  const handleEscSettings = useCallback(() => {
    const fallback = `/w/${workspaceId}/payments/invoices`;
    navigate(prevPathRef.current ?? fallback);
  }, [navigate, workspaceId]);

  useHotkeys(
    'escape',
    () => {
      if (navMode === 'settings') {
        handleEscSettings();
      }
    },
    [navMode, handleEscSettings]
  );

  useHotkeys('mod+/', () => {
    setLeftNavExpanded(!isLeftNavExpanded);
  });

  const twTransition = 'transition-all duration-100 menu-transition';

  return (
    <ToastProvider>
      <div
        className={clsx([
          'h-screen text-md flex flex-col overflow-x-hidden relative ',
        ])}
        data-testid="dashboard-layout"
      >
        <NewWorkspaceModal />
        <AmbientBlob />
        <div className={clsx(['flex flex-col h-full'])}>
          <div
            className={clsx([
              'h-full overflow-auto flex flex-row',
              twTransition,
            ])}
          >
            <div
              className={clsx([
                'group/nav',
                'sticky top-0 self-start shrink-0 z-10',
                isCollapsed ? 'w-f3' : 'w-f14',
                'flex flex-col overflow-visible scrollbar-hide',
                'border-r-[0.5px] border-main-300',
                'h-full',
                twTransition,
              ])}
            >
              {/* Static header — does not slide */}
              <div
                className={clsx(
                  'flex flex-col shrink-0',
                  isCollapsed
                    ? 'items-center justify-center h-[calc(4lh-0.5px)]'
                    : 'px-f1 h-[calc(4lh-0.5px)]'
                )}
              >
                <div
                  className={clsx(
                    'flex items-start pt-f1',
                    isCollapsed ? 'justify-center' : 'justify-between',
                    !isCollapsed && navMode === 'workspaces' ? '' : 'flex-1'
                  )}
                >
                  {!isCollapsed && navMode === 'settings' && (
                    <button
                      type="button"
                      className="text-xmd text-main-500 hover:text-main cursor-pointer font-helvetica"
                      onClick={handleEscSettings}
                      data-testid="nav-esc-button"
                    >
                      <span className="font-FragmentMono">←</span> Esc
                    </button>
                  )}
                  {!isCollapsed && navMode === 'workspaces' && (
                    <button
                      type="button"
                      className="text-xmd text-main-500 hover:text-main cursor-pointer font-helvetica"
                      onClick={() => setNavMode('menu')}
                      data-testid="nav-back-button"
                    >
                      <span className="font-FragmentMono">←</span> Back
                    </button>
                  )}
                  {!isCollapsed &&
                    navMode !== 'workspaces' &&
                    navMode !== 'settings' && (
                      <PaymentsWorkspaceSwitcher
                        onToggleMenu={() =>
                          setNavMode((prev) =>
                            prev === 'nav' ? 'menu' : 'nav'
                          )
                        }
                      />
                    )}
                  {!isCollapsed &&
                    navMode !== 'nav' &&
                    navMode !== 'settings' && (
                      <button
                        type="button"
                        className={clsx(
                          'text-main-500 hover:text-main cursor-pointer shrink-0 text-xmd font-helvetica',
                          twTransition
                        )}
                        onClick={() => setNavMode('nav')}
                        data-testid="nav-close-button"
                      >
                        ✕
                      </button>
                    )}
                  {isCollapsed && (
                    <button
                      type="button"
                      className="text-main-500 hover:text-main cursor-pointer shrink-0 text-xmd"
                      onClick={() => setLeftNavExpanded(!isLeftNavExpanded)}
                      data-testid="main-nav-collapse-button"
                    >
                      ☰
                    </button>
                  )}
                  {!isCollapsed && navMode === 'nav' && (
                    <button
                      type="button"
                      className={clsx(
                        'text-main-500 hover:text-main cursor-pointer shrink-0 text-xmd',
                        'opacity-0 group-hover/nav:opacity-100',
                        twTransition
                      )}
                      onClick={() => setLeftNavExpanded(!isLeftNavExpanded)}
                      data-testid="main-nav-collapse-button"
                    >
                      «
                    </button>
                  )}
                </div>
                {!isCollapsed && navMode === 'workspaces' && (
                  <div className="flex items-end flex-1 pb-f1">
                    <span className="text-xmd text-main font-helvetica truncate">
                      {workspace.name}
                    </span>
                  </div>
                )}
              </div>
              {/* Sliding content area */}
              {isCollapsed ? (
                <nav
                  data-testid="left-nav-closed"
                  className="border-t-[0.5px] border-t-main-300"
                >
                  <PaymentsNav workspaceId={workspace.id} isCollapsed />
                </nav>
              ) : (
                <div className="overflow-hidden flex-1 border-t-[0.5px] border-t-main-300">
                  <div
                    className="flex transition-transform duration-200 ease-in-out"
                    style={{
                      width: '400%',
                      transform: {
                        nav: 'translateX(0%)',
                        menu: 'translateX(-25%)',
                        workspaces: 'translateX(-50%)',
                        settings: 'translateX(-75%)',
                      }[navMode],
                    }}
                  >
                    {/* Panel 1: Nav */}
                    <nav
                      data-testid="left-nav-open"
                      className="w-1/4 shrink-0 px-f1"
                    >
                      <PaymentsNav
                        workspaceId={workspace.id}
                        isCollapsed={false}
                      />
                    </nav>

                    {/* Panel 2: Menu */}
                    <div className="w-1/4 shrink-0 px-f1">
                      <PaymentsNavMenu
                        navMode="menu"
                        onSwitchWorkspace={() => setNavMode('workspaces')}
                        onClose={() => setNavMode('nav')}
                      />
                    </div>

                    {/* Panel 3: Workspaces */}
                    <div className="w-1/4 shrink-0 px-f1 overflow-y-auto scrollbar-hide">
                      <PaymentsNavMenu
                        navMode="workspaces"
                        onSwitchWorkspace={() => setNavMode('workspaces')}
                        onClose={() => setNavMode('nav')}
                      />
                    </div>

                    {/* Panel 4: Settings */}
                    <div className="w-1/4 shrink-0 px-f1">
                      <PaymentsSettingsNav />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <main
              data-testid="layout-main"
              className={clsx([
                'w-full h-full scrollbar-hide grow',
                twTransition,
              ])}
            >
              {children}
            </main>
          </div>
          {footer && (
            <footer data-testid="footer" className="mt-auto sticky bottom-0">
              {footer}
            </footer>
          )}
        </div>
        {showLoadingIndicator && (
          <div className="absolute bottom-f2 z-50 right-f2 bg-opacity-100 flex flex-col justify-end">
            <LoadingIndicator />
          </div>
        )}
      </div>
    </ToastProvider>
  );
};
